/**
 * A die face showing activation cost (SPEC.md §8.3).
 *
 * Drawn rather than typed, so the frame can stay neutral while the pips carry the rarity
 * colour — and so it renders identically regardless of whether the device's font happens to
 * include U+2680–2685.
 *
 * Every face occupies the same box, including the blank one for "not set", so a column of
 * them lines up and a row of badges does not change height as the value changes.
 */

import { DIE_FRAME, ENERGY_COLORS, PIP_LAYOUTS } from "./energyPips";

/** The root font size the nominal `size` values were chosen against. */
const BASE_FONT_PX = 16;

export function Die({
  value,
  size = 15,
  title,
}: {
  /** 1–5, or null for "no activation cost set". */
  value: number | null;
  size?: number;
  title?: string;
}) {
  const pips = value === null ? [] : (PIP_LAYOUTS[value] ?? []);
  const colour = value === null ? DIE_FRAME : (ENERGY_COLORS[value] ?? DIE_FRAME);

  // A 24-unit box with pips on a 3×3 grid inset from the frame.
  //
  // The binding constraint is the frame, not the neighbouring pips: the closest pair on
  // any face is diagonal, so at this spacing they are 7.8 units apart and a radius of 3.2
  // leaves room to spare. Pips this size read as pips at 15px; the first attempt was
  // conservative for no reason and looked like dust.
  const inset = 6.5;
  const step = (24 - inset * 2) / 2;
  const pipRadius = 3.2;

  return (
    <svg
      // Emitted in rem so a die keeps its proportion to the text beside it when the reader
      // scales up. `size` stays in nominal pixels, which is how every caller thinks of it.
      width={`${size / BASE_FONT_PX}rem`}
      height={`${size / BASE_FONT_PX}rem`}
      viewBox="0 0 24 24"
      role="img"
      aria-label={title ?? (value === null ? "No energy set" : `Energy ${value}`)}
      className="die"
    >
      {title && <title>{title}</title>}
      <rect
        x="1.5"
        y="1.5"
        width="21"
        height="21"
        rx="5"
        fill="none"
        stroke={DIE_FRAME}
        strokeWidth="1.6"
        opacity={value === null ? 0.45 : 0.85}
      />
      {pips.map(([col, row]) => (
        <circle
          key={`${col}-${row}`}
          cx={inset + col * step}
          cy={inset + row * step}
          r={pipRadius}
          fill={colour}
        />
      ))}
    </svg>
  );
}
